import { loadWasmGame, type WasmGameInstance } from "../wasm/wasm";

export type OfflineProfile = {
  name: string;
  ballColor: string;
  nameColor: string;
};

export class OfflineGame {
  private wasmGame: WasmGameInstance | null = null;
  private inputSequence = 0;
  private lastInputX = 0;
  private lastInputY = 0;

  public async start(profile: OfflineProfile): Promise<void> {
    this.inputSequence = 0;
    this.lastInputX = 0;
    this.lastInputY = 0;

    this.wasmGame = await loadWasmGame(Date.now());

    this.wasmGame.addLocalPlayer(
      JSON.stringify({
        name: profile.name,
        ball_color: hexToRgb(profile.ballColor),
        name_color: hexToRgb(profile.nameColor)
      })
    );

    for (let index = 0; index < 4; index += 1) {
      this.safeTick(16);
    }
  }

  public tick(deltaMs: number): void {
    this.safeTick(Math.round(deltaMs));
  }

  public setInput(x: number, y: number): void {
    this.lastInputX = x;
    this.lastInputY = y;
    this.inputSequence += 1;

    try {
      this.wasmGame?.setInput(x, y, this.inputSequence);
    } catch {
      return;
    }
  }

  public clearInput(): void {
    this.lastInputX = 0;
    this.lastInputY = 0;
    this.inputSequence += 1;

    try {
      this.wasmGame?.setInput(0, 0, this.inputSequence);
    } catch {
      return;
    }
  }

  public chooseUpgrade(upgrade: unknown): void {
    try {
      this.wasmGame?.chooseUpgradeJson(JSON.stringify(upgrade));
    } catch {
      return;
    }
  }

  public respawn(): boolean {
    if (!this.wasmGame) {
      return false;
    }

    try {
      this.wasmGame.respawnLocalPlayer();
      this.clearInput();

      for (let index = 0; index < 2; index += 1) {
        this.safeTick(16);
      }

      return true;
    } catch {
      return false;
    }
  }

  public snapshot(): unknown | null {
    try {
      const json = this.wasmGame?.snapshotJson();

      if (!json) {
        return null;
      }

      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  private safeTick(deltaMs: number): void {
    try {
      this.wasmGame?.tick(Math.max(1, Math.min(50, deltaMs)));
    } catch {
      return;
    }
  }
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const normalized = hex.replace("#", "");
  const value = Number.parseInt(normalized, 16);

  if (!Number.isFinite(value) || normalized.length !== 6) {
    return { r: 66, g: 135, b: 245 };
  }

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255
  };
}